import { useEffect, useMemo, useRef, useState } from 'react';
import { atlasDefaultMode, type AtlasMode } from '../lib/atlasViewport';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { WORLD_BORDERS } from '../data/worldBorders';
import PageHeader from '../components/PageHeader';
import { withPageIcon } from '../lib/uiIcons';
import PageLoadError from '../components/PageLoadError';
import { useProfileContext } from '../hooks/useProfileContext';
import { apiGetShows, apiGetVenues, todayMinDate, type ShowDTO, type VenueDTO } from '../api';
// P2e — geocoding/haversine + the tour-stats badge are SHARED with the
// Day-of page's BEFORE mode (one calculation, no second copy that drifts).
import { isGeocoded, tourStatsOf, tourStatsBadge } from '../lib/tourStats';
import './AtlasPage.css';

/**
 * P2c — Atlas / Route map (maquette `sketches-refonte-ui-2026-10-09/006-vision-atlas`).
 *
 * Read-only page: shows + venues from the EXISTING `src/api.ts` wrappers, no
 * backend change, no new dependency (the map is a plain inline SVG).
 *
 * P2c-2 (user decisions 2026-10-09) — ONE column, top → bottom:
 *   1. HERO: the next show (fallback = last show when nothing is upcoming)
 *      merging the old "selected show" card AND its countdown, with a single
 *      "Open show →" action;
 *   2. LIST: the upcoming dates — a row click OPENS that show;
 *   3. MAP: last and smaller (~280px desktop / ~220px mobile) — a pin click
 *      OPENS that show too. There is no "selected show" state anymore, so the
 *      whole page uses one interaction model: click = open the show.
 *
 * Projection = equirectangular, mapped into `viewBox="0 0 100 100"
 * preserveAspectRatio="none"` so one formula works for Europe, the US, or
 * anything else.
 *
 * P2g (user decision 2026-10-09 « carte style Google Maps ») — the FRAME is
 * no longer the bbox of every date: it is centred on the next (or today's)
 * geocoded show with a ~400 km half-height, so a distant date (Chengdu) stops
 * crushing Europe into a corner. Two frames, same projection maths:
 *   - `local` (default): centred on the anchor show, half-height
 *     LOCAL_RADIUS_KM / zoom — the view around the next show;
 *   - `all`: the legacy bbox over EVERY date (padded ~10%), one click away;
 * `zoom` (useState, x1.6 per step, clamped [0.5, 8]) scales the ACTIVE frame
 * around its own centre. Local state only: it resets on a band change and is
 * never persisted (a map viewport is not screen state — no localStorage).
 *
 * The lng span follows the MEASURED box aspect (ResizeObserver on the map),
 * because degrees are not square: 1 degree of latitude is 111.32 km, 1 degree
 * of longitude only 111.32 * cos(lat) — otherwise a zoomed frame would draw
 * Switzerland five times too wide.
 *
 * Out-of-frame stops are PROJECTED but NOT RENDERED (fewer nodes): the map's
 * `overflow:hidden` cuts the route segments crossing the border, exactly like
 * a tiled map clips the tiles it does not show. Every segment between
 * consecutive dates is still drawn — the route itself is never broken.
 *
 * Route segments: shows sorted BY DATE, a segment only when BOTH endpoints are
 * geocoded (a non-geocoded show CUTS the line cleanly — such a show is absent
 * from the map but still listed). Segment state:
 *   - `route-done` up to the next upcoming geocoded stop,
 *   - `route-next` = the leg INTO that stop (animated),
 *   - `route-dim` = everything after it.
 *
 * Every visible string goes through `t()` (namespace `atlas.*`, ×3 locales);
 * no field is ever invented: a box with no real value is simply not rendered.
 */

const NOT_DATED = '9999-99-99';

// ---- P2g viewport -------------------------------------------------------
/** Half-height (radius) of the DEFAULT local frame, in km. */
const LOCAL_RADIUS_KM = 400;
/** km per degree of LATITUDE (equirectangular). */
const KM_PER_DEG = 111.32;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 8;
/** Scale factor per zoom step (Google-Maps-like, one notch = x1.6). */
const ZOOM_STEP = 1.6;
/** Inset, in % of the frame: a pin closer than this to the border has its
 *  dot/label cut by the map's overflow:hidden, so it is not rendered at all. */
const PIN_INSET_X = 4;
const PIN_INSET_Y = 6;
/** Box used before the ResizeObserver reports the real size (desktop CSS:
 *  280px tall). Only the ASPECT matters for the projection. */
const FALLBACK_BOX = { w: 1000, h: 280 };

const r2 = (n: number) => Math.round(n * 100) / 100;
const pad2 = (n: number) => String(Math.max(0, Math.floor(n))).padStart(2, '0');

/** Date sort key — undated shows sort LAST instead of first (''). */
const sortKey = (s: ShowDTO) => s.date || NOT_DATED;

interface Stop {
  show: ShowDTO;
  venue?: VenueDTO;
  x: number;
  y: number;
  label: string;
  labClass: string;
  past: boolean;
  isNext: boolean;
}

interface Segment {
  key: string;
  cls: 'done' | 'next' | 'up' | 'dim';
  d: string;
  /** Chronological index among UPCOMING segments (0 = the next one) — drives
   * the staggered flow animation so the links pulse in date order. */
  order?: number;
}

/** The projected frame, in degrees — exposed to QA as `data-atlas-frame`. */
interface Frame {
  latLo: number;
  latHi: number;
  lngLo: number;
  lngHi: number;
}

interface AtlasModel {
  sorted: ShowDTO[];
  venueById: Map<string, VenueDTO>;
  /** EVERY geocoded stop (badge/empty-state source). */
  stops: Stop[];
  /** Only the stops inside the current frame — what actually renders. */
  visibleStops: Stop[];
  /** Ghost country borders: every ring projected into the frame, one path. */
  borderPath: string;
  segments: Segment[];
  upcoming: ShowDTO[];
  nextShow?: ShowDTO;
  /** Show rendered by the hero: the next one, else the most recent. */
  hero?: ShowDTO;
  /** Current frame (local or all-dates) + the show it is centred on. */
  frame: Frame;
  anchor?: ShowDTO;
}

export default function AtlasPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { bandSlug } = useParams<{ bandSlug: string }>();
  const { profiles, activeProfileId } = useProfileContext();

  // URL slug wins (the band selector navigates to /band/:slug/…), the active
  // profile is the fallback — same rule as ShowsPage.
  const band = useMemo(() => {
    const fromUrl = bandSlug ? profiles.find((p) => p.slug === bandSlug) ?? null : null;
    const fromCtx = activeProfileId ? profiles.find((p) => p.id === activeProfileId) ?? null : null;
    return fromUrl || fromCtx;
  }, [profiles, bandSlug, activeProfileId]);
  const bandId = band?.id || '';

  const [shows, setShows] = useState<ShowDTO[]>([]);
  const [venues, setVenues] = useState<VenueDTO[]>([]);
  // Archives trim (2026-10-22): by DEFAULT the Atlas shows UPCOMING dates only
  // (min_date = today); the toggle loads the full history (past + archives).
  // Persisted so the choice survives reloads.
  const [includeArchives, setIncludeArchives] = useState(() => {
    try { return localStorage.getItem('bandz.atlas.archives') === '1'; } catch { return false; }
  });
  const toggleArchives = (on: boolean) => {
    setIncludeArchives(on);
    try { localStorage.setItem('bandz.atlas.archives', on ? '1' : '0'); } catch { /* ignore */ }
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // ---- P2g map viewport (LOCAL state: resets with the band, never saved) --
  // LOT M1 (2026-11-04) — sur un TÉLÉPHONE le cadre « local » (centré sur le
  // prochain show, LOCAL_RADIUS_KM) ne contient QUE ce show : une carte de
  // 390×260 n'affiche alors qu'une cible isolée et l'utilisateur conclut qu'il
  // n'y a « pas de carte » (mesuré sur 390×844). Défaut mobile = « toutes les
  // dates » (le tracé de la tournée est enfin lisible d'un coup d'œil) ; le
  // bureau garde le cadrage local, inchangé. Aucune préférence persistée : la
  // valeur de départ seulement, les boutons de la carte restent maîtres.
  const narrowScreen = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 768px)').matches
      : false;
  const defaultMode = (): AtlasMode => atlasDefaultMode(narrowScreen());
  const [mode, setMode] = useState<AtlasMode>(defaultMode);
  const [zoom, setZoom] = useState(1);

  // A new band = a new route = the default viewport (zoom 1).
  useEffect(() => {
    setMode(defaultMode());
    setZoom(1);
  }, [bandId]);

  // The projection needs the box ASPECT (width / height) to keep the frame
  // undistorted — measured on the real node (desktop / mobile / sidebar).
  const mapRef = useRef<HTMLElement | null>(null);
  const [box, setBox] = useState(FALLBACK_BOX);

  // No band-scoped request may leave before the scope id is resolved.
  //
  // Lot 3a — extracted into `load` so the shared error state can RE-RUN it (a
  // retry that re-fetches, never a hard page reload). `reloadToken` re-arms the
  // effect; the promise chain, the cancel guard and the data are unchanged.
  const [reloadToken, setReloadToken] = useState(0);
  useEffect(() => {
    if (!bandId) {
      setShows([]);
      setVenues([]);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([apiGetShows({ band: bandId, minDate: includeArchives ? undefined : todayMinDate() }), apiGetVenues()])
      .then(([s, v]) => {
        if (cancelled) return;
        setShows(Array.isArray(s) ? s : []);
        setVenues(Array.isArray(v) ? v : []);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bandId, reloadToken, includeArchives]);

  // Countdown ticks once a second (the only timer on this page).
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  const model: AtlasModel = useMemo(() => {
    const venueById = new Map<string, VenueDTO>(venues.map((v) => [String(v.id), v]));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayMs = today.getTime();
    const isPast = (s: ShowDTO) => !!s.date && new Date(s.date).getTime() < todayMs;

    const sorted = [...shows].sort((a, b) => {
      const ka = sortKey(a);
      const kb = sortKey(b);
      return ka < kb ? -1 : ka > kb ? 1 : 0;
    });

    const coordOf = (s: ShowDTO): { venue: VenueDTO; lat: number; lng: number } | null => {
      if (s.venue_id == null || s.venue_id === '') return null;
      const v = venueById.get(String(s.venue_id));
      if (!isGeocoded(v)) return null;
      return { venue: v, lat: Number(v.lat), lng: Number(v.lng) };
    };

    // Geocoded stops, in date order, with their index in `sorted`.
    const raws: { show: ShowDTO; venue: VenueDTO; lat: number; lng: number; idx: number }[] = [];
    sorted.forEach((s, idx) => {
      const c = coordOf(s);
      if (c) raws.push({ show: s, venue: c.venue, lat: c.lat, lng: c.lng, idx });
    });

    // ---- the legacy global bbox: EVERY date, padded ~10% ----------------
    // Still computed on every render: it IS the "All dates" view (and the
    // reference frame the QA compares against).
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;
    for (const r of raws) {
      if (r.lat < minLat) minLat = r.lat;
      if (r.lat > maxLat) maxLat = r.lat;
      if (r.lng < minLng) minLng = r.lng;
      if (r.lng > maxLng) maxLng = r.lng;
    }
    const latPad = (maxLat - minLat) * 0.1;
    const lngPad = (maxLng - minLng) * 0.1;
    const gLatLo = minLat - latPad;
    const gLatHi = maxLat + latPad;
    const gLngLo = minLng - lngPad;
    const gLngHi = maxLng + lngPad;

    // First upcoming GEOCODED stop -> the pin that pulses + the `route-next`
    // leg AND the centre of the default (local) frame.
    const nextRawIdx = raws.findIndex((r) => r.show.date && !isPast(r.show));
    const nextSortedIdx = nextRawIdx >= 0 ? raws[nextRawIdx].idx : -1;
    // No upcoming geocoded stop (all dates played): centre on the last one.
    const anchorRaw = nextRawIdx >= 0 ? raws[nextRawIdx] : raws[raws.length - 1];
    const anchorLat = anchorRaw ? anchorRaw.lat : 0;
    const anchorLng = anchorRaw ? anchorRaw.lng : 0;

    // ---- P2g — pick the ACTIVE frame ------------------------------------
    //   local: centred on the anchor show, half-height LOCAL_RADIUS_KM / zoom
    //   all  : the padded global bbox (the pre-P2g behaviour), zoomed around
    //          its own centre by the same `zoom`.
    const aspect = box.h > 0 ? box.w / box.h : FALLBACK_BOX.w / FALLBACK_BOX.h;
    // Degrees are not square: 1 deg of longitude is only 111.32 * cos(lat) km,
    // so the lng span is derived from the lat span + the box aspect, otherwise
    // a zoomed frame would stretch the map horizontally.
    const cosLat = Math.max(0.2, Math.abs(Math.cos((anchorLat * Math.PI) / 180)));
    const halfLat = (LOCAL_RADIUS_KM / KM_PER_DEG) / zoom;

    let latLo: number;
    let latHi: number;
    let lngLo: number;
    let lngHi: number;
    if (raws.length === 0) {
      // No geocoded stop at all: nothing to project (the map shows the
      // "no geolocated venue" note instead).
      latLo = 0;
      latHi = 1;
      lngLo = 0;
      lngHi = 1;
    } else if (mode === 'all') {
      const cLat = (gLatLo + gLatHi) / 2;
      const cLng = (gLngLo + gLngHi) / 2;
      const hLat = (gLatHi - gLatLo) / (2 * zoom);
      const hLng = (gLngHi - gLngLo) / (2 * zoom);
      latLo = cLat - hLat;
      latHi = cLat + hLat;
      lngLo = cLng - hLng;
      lngHi = cLng + hLng;
    } else {
      const lngHalf = (halfLat * aspect) / cosLat;
      latLo = anchorLat - halfLat;
      latHi = anchorLat + halfLat;
      lngLo = anchorLng - lngHalf;
      lngHi = anchorLng + lngHalf;
    }

    // NO clamp anymore: an out-of-frame stop keeps an out-of-frame coordinate
    // (it is simply not rendered — see `visibleStops` below).
    const project = (lat: number, lng: number) => ({
      x: lngHi === lngLo ? 50 : ((lng - lngLo) / (lngHi - lngLo)) * 100,
      y: latHi === latLo ? 50 : ((latHi - lat) / (latHi - latLo)) * 100,
    });

    const stops: Stop[] = raws.map((r, i) => {
      const { x, y } = project(r.lat, r.lng);
      const label = r.venue.city || r.venue.name || r.show.city || r.show.venue_name || '';
      // Label side heuristic so labels stay inside the frame and don't collide:
      // right edge → label on the left, bottom band → label above, else right.
      const labClass = x >= 68 ? 'lab-left' : x > 22 && y >= 72 ? 'lab-above' : '';
      return {
        show: r.show,
        venue: r.venue,
        x,
        y,
        label,
        labClass,
        past: isPast(r.show),
        isNext: i === nextRawIdx,
      };
    });

    // P2g — only the stops INSIDE the frame are rendered (fewer nodes). The
    // PIN_INSET_* margin keeps a rendered pin's dot + label whole: anything
    // closer to the border than the inset would be cut by overflow:hidden.
    const visibleStops = stops.filter(
      (s) =>
        s.x >= PIN_INSET_X &&
        s.x <= 100 - PIN_INSET_X &&
        s.y >= PIN_INSET_Y &&
        s.y <= 100 - PIN_INSET_Y,
    );

    // ---- ghost country borders (user 10-10): the pins used to float on an
    //      empty sheet. ONE path node carries every ring on earth — the frame
    //      clips whatever overflows, and the same equirectangular project()
    //      keeps the outlines exactly aligned with the pins and the route.
    const borderPath = WORLD_BORDERS.map((ring) => {
      let d = '';
      for (let i = 0; i < ring.length; i += 2) {
        const p = project(ring[i + 1], ring[i]);
        d += (i === 0 ? 'M' : 'L') + r2(p.x) + ',' + r2(p.y);
      }
      return d + 'Z';
    }).join(' ');

    // ---- route segments: consecutive shows BY DATE, cut when either side
    //      has no coordinates (the tour km total is NOT accumulated here:
    //      it belongs to the shared tourStatsOf — one source for the badge)
    const segments: Segment[] = [];
    let upOrder = 0;
    for (let i = 0; i < sorted.length - 1; i++) {
      const a = coordOf(sorted[i]);
      const b = coordOf(sorted[i + 1]);
      if (!a || !b) continue; // clean cut — the line never crosses a missing stop
      const j = i + 1;
      const cls: Segment['cls'] =
        nextSortedIdx === -1 ? 'dim' : j < nextSortedIdx ? 'done' : j === nextSortedIdx ? 'next' : 'up';
      const p1 = project(a.lat, a.lng);
      const p2 = project(b.lat, b.lng);
      const mx = (p1.x + p2.x) / 2;
      const my = (p1.y + p2.y) / 2;
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const len = Math.hypot(dx, dy) || 1;
      const bow = Math.min(6, len * 0.08); // mockup-style arc, deterministic
      const cx = r2(mx + (-dy / len) * bow);
      const cy = r2(my + (dx / len) * bow);
      const isUp = cls === 'up' || cls === 'next';
      upOrder += isUp ? 1 : 0;
      segments.push({
        key: `${String(sorted[i].id)}_${String(sorted[j].id)}`,
        cls,
        d: `M${r2(p1.x)},${r2(p1.y)} Q${cx},${cy} ${r2(p2.x)},${r2(p2.y)}`,
        order: isUp ? upOrder - 1 : undefined,
      });
    }

    const upcoming = sorted.filter((s) => s.date && !isPast(s));
    const nextShow = upcoming[0];
    // Hero target: next show, else the most recent one (nothing to count down).
    const hero = nextShow ?? sorted[sorted.length - 1];

    return {
      sorted,
      venueById,
      stops,
      visibleStops,
      borderPath,
      segments,
      upcoming,
      nextShow,
      hero,
      frame: { latLo, latHi, lngLo, lngHi },
      anchor: anchorRaw ? anchorRaw.show : undefined,
    };
    // `box`/`mode`/`zoom` change the FRAME, not the data.
  }, [shows, venues, box, mode, zoom]);

  // ---- measure the map box: the projection needs its ASPECT --------------
  // Re-observed when the map mounts/unmounts (band switch, empty state).
  const mapAttached = !!bandId && !loading && model.sorted.length > 0;
  useEffect(() => {
    if (!mapAttached) return;
    const el = mapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const read = () => {
      const r = el.getBoundingClientRect();
      const w = Math.round(r.width);
      const h = Math.round(r.height);
      if (w <= 0 || h <= 0) return;
      setBox((b) => (b.w === w && b.h === h ? b : { w, h }));
    };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    window.addEventListener('resize', read);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', read);
    };
  }, [mapAttached]);

  // ---- countdown ----------------------------------------------------------
  const countdown = useMemo(() => {
    const s = model.nextShow;
    if (!s?.date) return null;
    const hm = /^(\d{1,2}:\d{2})/.exec(String(s.door_time || ''));
    const iso = hm ? `${s.date}T${hm[1]}` : `${s.date}T00:00`;
    let target = new Date(iso).getTime();
    if (!Number.isFinite(target)) target = new Date(s.date).getTime();
    const remain = Math.max(0, target - now);
    return {
      show: s,
      h: Math.floor(remain / 3600000),
      m: Math.floor(remain / 60000) % 60,
      sec: Math.floor(remain / 1000) % 60,
    };
  }, [model.nextShow, now]);

  // ---- formatting helpers (locale-driven, never a hardcoded word) ---------
  const dayNum = (d?: string) => (d ? new Date(d).toLocaleDateString(i18n.language, { day: '2-digit' }) : '');
  const monthShort = (d?: string) => (d ? new Date(d).toLocaleDateString(i18n.language, { month: 'short' }) : '');
  const dateLong = (d?: string) =>
    d
      ? new Date(d).toLocaleDateString(i18n.language, { weekday: 'short', day: '2-digit', month: 'short' })
      : '';

  const heroShow = model.hero;
  const heroVenue = heroShow ? model.venueById.get(String(heroShow.venue_id ?? '')) : undefined;
  const heroCity = heroShow?.city || heroVenue?.city || '';
  const heroCountry = heroShow?.country || heroVenue?.country || '';
  const heroIsNext = !!heroShow && !!model.nextShow && String(heroShow.id) === String(model.nextShow.id);
  const refDay = new Date();
  refDay.setHours(0, 0, 0, 0);
  const heroPast = !!heroShow?.date && new Date(heroShow.date).getTime() < refDay.getTime();
  const heroState = !heroShow
    ? ''
    : heroIsNext
      ? t('atlas.stateNext')
      : heroPast
        ? t('atlas.statePlayed')
        : t('atlas.stateUpcoming');
  const heroPlace = [heroCity, heroCountry].filter(Boolean).join(', ');
  const heroMeta = [
    dateLong(heroShow?.date),
    heroPlace,
    heroShow?.door_time ? t('atlas.doors', { time: heroShow.door_time }) : '',
  ]
    .filter(Boolean)
    .join(' · ');

  // Real fields only — a box with no value is not rendered (never invented).
  const heroBoxes: { label: string; value: string }[] = [];
  if (heroShow) {
    if (heroShow.status) heroBoxes.push({ label: t('atlas.statStatus'), value: heroShow.status });
    if (heroShow.tour_name) heroBoxes.push({ label: t('atlas.statTour'), value: heroShow.tour_name });
    if (heroCity) heroBoxes.push({ label: t('atlas.statCity'), value: heroCity });
    if (heroCountry) heroBoxes.push({ label: t('atlas.statCountry'), value: heroCountry });
  }

  // Surface viewer v2 (2026-10-20): the Route map is the VIEWER's only Tour page —
  // clicking an event opens the show's DAYSHEET read view. (2026-10-25, user):
  // the Route map is the "consultation" entry — NO ?edit=1 here (the ✏️ Edit
  // button on the read view stays one click away); the Shows LIST is the
  // "administration" entry (its links open ?edit=1 directly).
  const openShow = (id: string | number) => navigate(`/tour/shows/${String(id)}`);

  const nextVenueName = countdown
    ? model.venueById.get(String(countdown.show.venue_id ?? ''))?.name ||
      countdown.show.venue_name ||
      countdown.show.name ||
      ''
    : '';
  // P2e — the badge numbers/rule live in src/lib/tourStats.ts, shared with
  // the Day-of page's BEFORE mode (same strings, ONE calculation).
  const stats = useMemo(() => tourStatsOf(shows, venues), [shows, venues]);
  const statsBadge = tourStatsBadge(stats, i18n.language);
  const badge = t(statsBadge.key, statsBadge.vars);

  return (
    <div className="page atlas-page" data-page="atlas">
      <PageHeader
        title={withPageIcon('atlas.title', t('atlas.title'))}
        subtitle={band?.name}
        meta={
          stats.stops > 0 ? (
            <span className="atlas-headstats" data-atlas-headstats>
              {badge}
            </span>
          ) : null
        }
        actions={
          <div className="atlas-legend" data-atlas-legend>
            <span>
              <i className="lg-done" />
              {t('atlas.statePlayed')}
            </span>
            <span>
              <i className="lg-next" />
              {t('atlas.stateNext')}
            </span>
            <span>
              <i className="lg-up" />
              {t('atlas.stateUpcoming')}
            </span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 'var(--font-md)' }}>
              <input type="checkbox" data-include-archives checked={includeArchives} onChange={(e) => toggleArchives(e.target.checked)} />
              {t('shows.includeArchives')}
            </label>
          </div>
        }
      />

      {/* Lot 3a: the shows+venues read failed → no map, no hero, no dates. The
          shared state says so and offers a real retry (it re-fetches). The
          page's own `.atlas-error` line is NOT used for this: it cannot retry. */}
      {error && (
        <PageLoadError
          title={withPageIcon('atlas.title', t('atlas.title'))}
          detail={error}
          onRetry={() => setReloadToken((n) => n + 1)}
        />
      )}

      {error ? null : !bandId ? (
        <p className="atlas-empty" data-atlas-empty>
          {t('atlas.noBand')}
        </p>
      ) : loading ? (
        <p className="text-dim">{t('common.loading')}</p>
      ) : model.sorted.length === 0 ? (
        <p className="atlas-empty" data-atlas-empty>
          {t('atlas.noShows')}
        </p>
      ) : (
        <div className="atlas-stack">
          {/* ======================= 1 · NEXT SHOW HERO =================== */}
          <section className="atlas-hero" data-atlas-hero>
            <div className="atlas-hero-body">
              <div className="atlas-hero-state" data-atlas-state>
                <span className="led" />
                <span>
                  {heroState}
                  {heroShow?.date ? ` · ${dateLong(heroShow.date)}` : ''}
                </span>
              </div>
              <div className="atlas-hero-venue" data-atlas-venue>
                {heroVenue?.name || heroShow?.name || heroCity || '—'}
              </div>
              <div className="atlas-hero-meta">{heroMeta || '—'}</div>
              {heroBoxes.length > 0 && (
                <div className="atlas-hero-grid">
                  {heroBoxes.map((b) => (
                    <div className="b" key={b.label}>
                      <div className="g">{b.label}</div>
                      <div className="val">{b.value}</div>
                    </div>
                  ))}
                </div>
              )}
              {/* One action only (user decision 2026-10-09): the show page's
                  default tab IS the day sheet, so a second button would point
                  to the exact same URL. */}
              <div className="atlas-hero-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  data-atlas-open
                  onClick={() => heroShow && openShow(heroShow.id)}
                >
                  {t('atlas.openShow')}
                </button>
              </div>
            </div>

            {countdown && (
              <div className="atlas-hero-cd">
                <div className="atlas-capline">
                  {t('atlas.countdownLabel', { venue: nextVenueName })}
                </div>
                <div className="atlas-cd" data-atlas-cd>
                  <div className="u">
                    <div className="n" data-atlas-cd-h>
                      {pad2(countdown.h)}
                    </div>
                    <div className="l">{t('atlas.hours')}</div>
                  </div>
                  <div className="u">
                    <div className="n" data-atlas-cd-m>
                      {pad2(countdown.m)}
                    </div>
                    <div className="l">{t('atlas.minutes')}</div>
                  </div>
                  <div className="u">
                    <div className="n" data-atlas-cd-s>
                      {pad2(countdown.sec)}
                    </div>
                    <div className="l">{t('atlas.seconds')}</div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* ======================= 2 · DATES LIST ======================= */}
          <section className="atlas-card" data-atlas-listcard>
            <div className="atlas-card-hd">
              <div className="t">{t('atlas.route')}</div>
              <button type="button" className="a" onClick={() => navigate('/tour/shows')}>
                {t('atlas.allShows')}
              </button>
            </div>
            {model.upcoming.length === 0 ? (
              <p className="atlas-list-empty" data-atlas-list-empty>
                {t('atlas.noUpcoming')}
              </p>
            ) : (
              <div className="atlas-list" data-atlas-list>
                {model.upcoming.map((s) => {
                  const id = String(s.id);
                  const v = model.venueById.get(String(s.venue_id ?? ''));
                  const isNextShow = !!model.nextShow && String(model.nextShow.id) === id;
                  const place = [s.city || v?.city || '', s.country || v?.country || '']
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <button
                      key={id}
                      type="button"
                      className="atlas-row"
                      data-atlas-row={id}
                      onClick={() => openShow(s.id)}
                    >
                      <span className="dt">
                        {dayNum(s.date)}
                        <small>{monthShort(s.date)}</small>
                      </span>
                      <span className="mid">
                        <span className="v">{v?.name || s.venue_name || s.name || '—'}</span>
                        <span className="c">{place}</span>
                      </span>
                      <span className={`atlas-tag${isNextShow ? ' next' : ''}`}>
                        {isNextShow ? t('atlas.stateNext') : s.status || ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {/* ======================= 3 · MAP (bottom) ===================== */}
          <section
            className="atlas-map"
            data-atlas-map
            data-atlas-mode={mode}
            data-atlas-zoom-level={r2(zoom)}
            data-atlas-frame={`${r2(model.frame.latLo)} ${r2(model.frame.latHi)} ${r2(
              model.frame.lngLo,
            )} ${r2(model.frame.lngHi)}`}
            ref={mapRef}
          >
            {/* P3e (user decision): the tour-stats badge rendered TWICE on this
                page (header meta + map overlay) is now ONE — the header's, which
                is also the position the Day-of page carries. The map keeps its
                scan/legend, not a second copy of the same numbers. */}
            <div className="atlas-scan" aria-hidden="true" />
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
              {/* Ghost borders — context layer, painted first so the route and
                  the pins always sit on top of it. */}
              <path className="atlas-borders" data-atlas-borders d={model.borderPath} />
              {model.segments.map((s) => (
                <path key={s.key} className={`atlas-seg atlas-seg-${s.cls}`} data-atlas-seg={s.cls} d={s.d}
                      style={s.order != null ? { animationDelay: `${(s.order % 6) * 0.25}s` } : undefined} />
              ))}
            </svg>
            {model.stops.length === 0 && (
              <p className="atlas-map-note" data-atlas-nopins>
                {t('atlas.noGeocoded')}
              </p>
            )}
            {model.visibleStops.map((st) => {
              const id = String(st.show.id);
              const cls = ['atlas-pin', st.past ? 'done' : '', st.isNext ? 'next' : '', st.labClass]
                .filter(Boolean)
                .join(' ');
              return (
                <button
                  key={id}
                  type="button"
                  className={cls}
                  data-atlas-pin={id}
                  style={{ left: `${st.x}%`, top: `${st.y}%` }}
                  aria-label={st.label || undefined}
                  onClick={() => openShow(st.show.id)}
                >
                  <span className="dot" />
                  {st.label && <span className="lab">{st.label}</span>}
                </button>
              );
            })}
            <div className="atlas-mapctl" data-atlas-mapctl>
              <div className="grp">
                <button
                  type="button"
                  data-atlas-zoom="in"
                  title={t('atlas.zoomIn')}
                  aria-label={t('atlas.zoomIn')}
                  onClick={() => setZoom((z) => Math.min(ZOOM_MAX, r2(z * ZOOM_STEP)))}
                >
                  +
                </button>
                <button
                  type="button"
                  data-atlas-zoom="out"
                  title={t('atlas.zoomOut')}
                  aria-label={t('atlas.zoomOut')}
                  onClick={() => setZoom((z) => Math.max(ZOOM_MIN, r2(z / ZOOM_STEP)))}
                >
                  −
                </button>
              </div>
              <button
                type="button"
                data-atlas-recenter
                title={t('atlas.recenter')}
                aria-label={t('atlas.recenter')}
                onClick={() => {
                  setMode('local');
                  setZoom(1);
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="12" cy="12" r="6" />
                  <path d="M12 1.5v5M12 17.5v5M1.5 12h5M17.5 12h5" />
                </svg>
              </button>
              <button
                type="button"
                className="pill"
                data-atlas-alldates
                title={t('atlas.allDates')}
                aria-label={t('atlas.allDates')}
                onClick={() => {
                  setMode('all');
                  setZoom(1);
                }}
              >
                {t('atlas.allDates')}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
